import { useStore } from 'TourApp/store'
import React, { useMemo, useState } from 'react'
import { ReactComponent as ArrowDown } from 'shared/assets/icons/arrow-down-small-navbar.svg'
import PropertyTourDetailsDropdown from '../PropertyTourDetailsDropdown'
import PropertyDescriptionDetailsDropdown, { getUnitDetailsLabel } from '../PropertyDescriptionDetailsDropdown'
import { events, posttour } from 'shared/analytics'
import { useSwipeable } from 'react-swipeable'
// Images
import sparxLogo from 'shared/assets/images/sparx-logo.png'
import { useNavigate } from '@tanstack/react-location'
import { useApartment } from 'TourApp/hooks/useApartment'
import { useProperty } from 'TourApp/hooks/useProperty'
import { Disclaimer } from 'Resident/layouts/SidebarLayout'

export default function Navbar({ className = '' }) {
  const { tourData, selectedApartmentId, isLineApp, hideUnit, setSelectedApartmentId } = useStore()
  const { units } = tourData
  const [menuOpen, setMenuOpen] = useState(false)
  const navigate = useNavigate()

  const handlers = useSwipeable({
    onSwipedDown: () => {
      setMenuOpen(!menuOpen)
    },
    preventScrollOnSwipe: true,
  })

  const apartment = useApartment()

  function getTourDetailsTitle() {
    return units[0].unit.unit_type.property.property_name
  }

  function onToggle(name, item) {
    if (item && selectedApartmentId !== item.id) {
      setSelectedApartmentId(item.id)
      posttour(events.mas)
      navigate({ to: '/gallery' })
    }
  }

  const property = useProperty()

  function getPropertyTitle() {
    if (property.properties_configuration_options.building_as_property) {
      return apartment.unit.building_name
    } else {
      return property.property_name
    }
  }

  return (
    <>
      <div
        className={`fixed w-full bg-white z-40 flex justify-between items-center h-[72px] py-[16px] px-[24px] flex-shrink-0 border-b-[1px] border-[#C7CCD9] border-solid ${className}`}
      >
        {/* Left Section */}
        <div className="flex-1 min-w-0 max-w-[80%] pr-[24px]">
          <div className="font-semibold text-[16px] truncate" title={getPropertyTitle()}>
            {getPropertyTitle()}
          </div>
          <div
            className="font-medium text-[14px] mt-[2px] text-[#5F6787] truncate"
            title={apartment.unit.unit_type.property.real_estate_company.company_name}
          >
            {apartment.unit.unit_type.property.real_estate_company.company_name}
          </div>
        </div>

        {/* Right Section */}
        <button type="button" onClick={() => setMenuOpen(!menuOpen)} className="block flex-shrink-0 max-w-[50%]">
          <div
            className={`min-w-[90px] h-[40px] bg-[#F5F5F8] rounded-full flex items-center ${
              units.length > 1 ? 'justify-between' : 'justify-center'
            } px-[16px] overflow-hidden`}
          >
            <span className="font-semibold text-[16px] block truncate min-w-0">
              {getUnitDetailsLabel(apartment.unit, apartment.unit.unit_type, isLineApp)}
            </span>
            {units.length > 1 && (
              <div className="ml-[16px] flex-shrink-0">
                <ArrowDown />
              </div>
            )}
          </div>
        </button>
      </div>
      {/* Bottom sheet */}
      <div>
        {menuOpen && (
          <div
            className="absolute top-0 left-0 h-screen w-screen z-[100] bg-black opacity-30"
            onClick={() => {
              setMenuOpen(!menuOpen)
            }}
          ></div>
        )}
        <div
          className={`fixed bottom-0 left-0 z-[100] w-full ${
            menuOpen ? 'max-h-full' : 'max-h-0'
          } overflow-hidden transition-all ease-out duration-500`}
        >
          <div className="bg-[#fff] p-[40px] pt-[0] rounded-t-[16px]">
            <div className="flex items-center justify-center bg-transparent w-full h-[36px] mb-[10px]" {...handlers}>
              <div className="absolute h-[4px] w-[32px] bg-[#E5E7ED] rounded-full"></div>
            </div>
            <div>
              <div className="text-[#C7CCD9] uppercase text-[14px] font-semibold">property</div>
              <PropertyTourDetailsDropdown
                plain
                details={{
                  unit: apartment.unit,
                  startTime: tourData.start_time,
                  agent: tourData.agent,
                  title: getTourDetailsTitle(),
                }}
                name="propertytourdetailsdropdown"
              />
            </div>
            <div className="mt-[16px]">
              <div className="text-[#C7CCD9] uppercase text-[14px] font-semibold">units</div>
              <div>
                {tourData.units.map(({ unit }, index) => (
                  <div
                    className={`relative pt-[5px] ${unit.id !== apartment.unit.id ? 'pb-[5px]' : ''} ${
                      units.length > 1 ? 'border-b-[1px] border-solid border-[#C7CCD9]' : ''
                    }`}
                  >
                    {apartment.unit.id === unit.id && tourData.units.length > 1 && (
                      <div className="h-[6px] w-[6px] bg-black rounded-full left-[-14px] top-[22px] absolute"></div>
                    )}
                    <PropertyDescriptionDetailsDropdown
                      details={unit}
                      className="w-full"
                      plain
                      active={unit.id === apartment.unit.id}
                      lineOnly={isLineApp}
                      key={index}
                      onToggle={(name) => {
                        onToggle(name, unit)
                      }}
                      hideUnit={hideUnit}
                      name={`apartment${unit.id}`}
                      highlighted={selectedApartmentId === unit.id}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="h-[60px] bg-[#fafafa] border-t-[1px] border-solid border-[#E5E7ED] w-full flex flex-col items-center justify-center">
            <div className="flex items-center">
              <div className="text-[12px] font-medium text-[#5F6787]">Virtually Staged by</div>
              <img src={sparxLogo} alt={'sparx_logo'} className="h-[16px] mx-[8px]" />
            </div>
            <div className="flex items-center mt-[6px]">
              <span className="text-[#C7CCD9] text-[10px] leading-[18px]">
                © All Rights Reserved, SparX Inc. {new Date().getFullYear()}
              </span>
              <Disclaimer className="text-[10px] text-[#C7CCD9] ml-[5px]" />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
